/**
 * @fileoverview A collection event: an ordered index of pages that together are
 * a site, and the one place a private member may be named.
 *
 * Kind 33304, ours. See `kinds.ts` for why it stopped being a community spec's
 * kind on 2026-09-16 and what that reversal paid for.
 *
 * ---
 *
 * THE ORDER IS THE PRODUCT. A collection is not a set. An author who put
 * "Getting started" before "Reference" meant it, so members are held in tag
 * order and nothing here sorts by date or by name. A list that re-sorts itself
 * is a list the author cannot arrange.
 *
 * ---
 *
 * A PRIVATE MEMBER'S ADDRESS IS PART OF WHAT IS HIDDEN, and that decides the
 * whole shape of this file.
 *
 * It is tempting to put every member in a plaintext `a` tag and encrypt only the
 * keys, because a key is the secret and an address is just a name. That is
 * wrong. An address names an author, a kind and an identifier, and the list of
 * addresses is the shape of the private site: how many private pages there are,
 * where they sit in the order, and, for any page whose name was chosen by a
 * human rather than generated, what they are about. A reader who cannot open one
 * would still learn all of it.
 *
 * So a private member is named ONLY inside the encrypted content, never in a
 * tag. That is also why a page's name is opaque by default: the two defences
 * are the same defence, applied at two levels.
 *
 * ---
 *
 * THE ENCRYPTED HALF CARRIES THE WHOLE ORDERED LIST, NOT JUST THE PRIVATE PART.
 * This is a decision taken here rather than inherited, so it is worth the
 * paragraph.
 *
 * The superseded design had two events, a public index and a private overlay,
 * and therefore needed a merge rule: each private entry named a public member to
 * sit after, with a fallback for an anchor that no longer existed. The reversal
 * to one event was supposed to delete that rule, and simply moving the private
 * rows into this event's content does NOT delete it: the two halves are still
 * two lists that have to be interleaved, and the anchor can still go stale
 * between edits.
 *
 * Carrying the FULL order in the encrypted half deletes it properly. A reader
 * with the key uses that list and never interleaves anything. A reader without
 * the key uses the plaintext tags, which are already in order. Neither reader
 * merges, so there is no anchor to go stale and no fallback to get wrong.
 *
 * The cost is that public addresses appear twice in one event, once in the clear
 * and once inside the ciphertext. For a list of coordinates that is nothing, and
 * it buys the deletion of the one rule in this design that had a failure mode
 * nobody could see.
 *
 * ---
 *
 * WHAT A READER WITHOUT THE KEY SEES, stated plainly because it is a promise:
 * a real collection with its public members in their real order, and no
 * indication of how much is missing. Not a gap, not a count, not a placeholder.
 * The event's content being non-empty does reveal that SOMETHING is encrypted
 * here, which is the same existence leak already accepted for a private page and
 * is not made worse by anything in this file.
 */

import type { ContentKey, KeySetEntry } from '@cloistr/auth/core';
import type { UnsignedEvent } from 'nostr-tools';

import {
  type CollectionMember,
  OWNER_KEY_TAG,
  openMemberKeys,
  sealMemberKeys,
} from './collectionKeys';
import { PAGE_COLLECTION_KIND } from './kinds';
import { decryptWithPageKey, encryptWithPageKey } from './pageKey';

/** One member: where it lives, what to call it, and where to look for it. */
export interface CollectionMemberRef {
  /** The addressable coordinate, `kind:pubkey:d`. The kind is READ, never assumed. */
  address: string;
  /**
   * A DISPLAY LABEL, and the member page stays authoritative.
   *
   * Carried rather than fetched because the alternative is a relay round trip
   * per member on every view of a site, which is what potato-grade-design.md
   * exists to forbid: a forty-page handbook would open forty connections to
   * render a list.
   *
   * IT GOES STALE WHEN A PAGE IS RENAMED, and that is the accepted cost. It
   * refreshes on the next write, which is every add, removal or reorder, so
   * staleness is bounded by how often the collection changes rather than
   * unbounded. Drift you can see and that heals itself beats a design that is
   * always correct and too slow to use.
   */
  title?: string;
  /** A relay hint, carried so a member on somebody else's relay is findable. */
  relay?: string;
  /** Whether this member is named in the clear or only inside the ciphertext. */
  private: boolean;
}

/** A collection as the app holds it, before or after the relay. */
export interface Collection {
  /** The `d` tag. Permanent, and the reason the collection's address survives edits. */
  identifier: string;
  title: string;
  summary?: string;
  /** In DISPLAY ORDER, public and private together. */
  members: CollectionMemberRef[];
  /** The landing page's address. Must also appear in `members`. */
  front?: string;
  /** A theme address; one reference styles every page in the collection. */
  theme?: string;
  /** Spec-suggested vocabulary, e.g. "documentation". */
  type?: string;
}

/** What a reader got, and what it could not open. */
export interface ParsedCollection extends Collection {
  /** True when the event carried an encrypted half this reader could not open. */
  hasUnreadablePrivateHalf: boolean;
  /**
   * Each member's page key, by address, for a reader who opened the sealed half.
   *
   * THIS IS WHAT MAKES A SHARED COLLECTION WORTH SHARING. A recipient who can
   * open this collection can already open every member in it; before this map
   * existed, nothing looked, and every private member read as unavailable to
   * everyone except its author.
   *
   * EMPTY IS AN ORDINARY ANSWER, not a failure: a collection of public pages has
   * no key to carry, and a reader without the collection key has nothing to open.
   * `unopenedMemberKeys` is what tells those two apart.
   */
  memberKeys: Map<string, ContentKey>;
  /**
   * Addresses whose key row was present and would not open.
   *
   * Reported rather than thrown, for the reason `openMemberKeys` gives: one
   * unreadable row is one page you no longer have access to, and denying the
   * reader the other thirty-nine over it would be the wrong trade.
   */
  unopenedMemberKeys: string[];
}

interface SealedHalf {
  /** The COMPLETE ordered member list, so no reader ever merges two lists. */
  members: CollectionMemberRef[];
  /**
   * Each member's page key, wrapped under the collection key.
   *
   * IN HERE RATHER THAN IN A TAG, for the reason the file comment gives about
   * addresses: a row pairs an address with the fact that the page at it is
   * encrypted, and for a hidden member that pair is exactly what is being
   * hidden. `collectionKeys.ts` carries the longer form.
   *
   * SEPARATE FROM `members` RATHER THAN A FIELD ON EACH ENTRY, because the
   * envelope binds to the address it opens and a key set is what the shared
   * primitive seals and opens. Folding it into the member entry would put a
   * subject inside a record that already has one and invite them to diverge.
   */
  mkeys?: KeySetEntry[];
  /**
   * The title and summary, carried here as well as in the tags.
   *
   * REQUIRED rather than redundant for a collection with no public member: that
   * event publishes its name and nothing else, so the sealed half is the ONLY
   * place its title exists. Carrying them unconditionally keeps one shape for
   * both cases instead of a field that is sometimes load-bearing.
   */
  title: string;
  summary?: string;
}

/**
 * Build the tags for a collection event.
 *
 * `collectionKey` is required only when the collection has private members. A
 * wholly public collection is a plaintext event with empty content, which is
 * what most collections will be.
 *
 * MEMBER KEYS GO IN THE SEALED HALF AND NOWHERE ELSE. They are wrapped under the
 * collection key by `collectionKeys.ts`, and a private member's key row belongs
 * inside the ciphertext for the same reason its address does. This function used
 * to accept pre-built tag ROWS and filter them to public members, which was a
 * filter that silently dropped exactly the rows that mattered.
 */
export function buildCollectionTags(
  collection: Collection,
  options: {
    collectionKey: ContentKey;
    /** Each member's page key. Sealed into the encrypted half, never a tag. */
    memberKeys?: readonly CollectionMember[];
    ownerKeyTag?: string[];
  }
): { tags: string[][]; content: string } {
  if (!options.collectionKey) {
    throw new Error('A collection needs its collection key to be written');
  }

  const hasPublicMember = collection.members.some((m) => !m.private);

  // The sealed half, ALWAYS. See the file comment: if only collections with
  // something to hide carried content, the presence of content would itself be
  // the disclosure.
  const sealed: SealedHalf = { members: collection.members, title: collection.title };
  if (collection.summary) sealed.summary = collection.summary;
  // KEYS FOR MEMBERS THIS COLLECTION ACTUALLY HAS. A key row for an address that
  // was removed from the list would keep handing recipients a key to a page the
  // author has taken out of the site, which is the quiet half of un-sharing.
  if (options.memberKeys?.length) {
    const listed = new Set(collection.members.map((m) => m.address));
    const kept = options.memberKeys.filter((m) => listed.has(m.address));
    if (kept.length) sealed.mkeys = sealMemberKeys(kept, options.collectionKey);
  }
  const content = encryptWithPageKey(JSON.stringify(sealed), options.collectionKey);

  const tags: string[][] = [['d', collection.identifier]];

  // A COLLECTION WITH NO PUBLIC MEMBER PUBLISHES ITS NAME AND NOTHING ELSE.
  // Not the title, not the summary, not the theme. A reader without the key
  // gets an honest bare event rather than a titled empty site, which is the
  // same shape a private page degrades to.
  if (!hasPublicMember) {
    if (options.ownerKeyTag) tags.push(options.ownerKeyTag);
    return { tags, content };
  }

  tags.push(['title', collection.title]);
  if (collection.summary) tags.push(['summary', collection.summary]);
  if (collection.type) tags.push(['type', collection.type]);

  for (const m of collection.members) {
    if (m.private) continue;
    const row = ['a', m.address];
    if (m.relay) row.push(m.relay);
    tags.push(row);
    // The label rides beside the coordinate rather than inside it, so a client
    // that does not know the tag still gets a usable address.
    if (m.title) tags.push(['alabel', m.address, m.title]);
  }

  // `front` is emitted only when it names a PUBLIC member. Naming a private
  // page here would leak the one address the author most likely cared about
  // hiding, and it is the front door, so it is the first one a guesser tries.
  if (collection.front) {
    const target = collection.members.find((m) => m.address === collection.front);
    if (target && !target.private) tags.push(['front', collection.front]);
  }

  if (collection.theme) tags.push(['theme', collection.theme]);
  if (options.ownerKeyTag) tags.push(options.ownerKeyTag);

  assertCollectionConsistent(tags, content, options.collectionKey);
  return { tags, content };
}

/**
 * Refuse a collection whose two views of its own order disagree.
 *
 * THE FAILURE THIS CATCHES IS INVISIBLE FROM EITHER SIDE ALONE, which is what
 * makes it worth a check rather than a comment. There are two orderings in one
 * event: the plaintext `a` tags, and the public subset of the sealed list.
 * Nothing in the format forces them to agree. A publisher that rewrites one and
 * not the other gives a reader WITH the key and a reader WITHOUT it two
 * different sites, and neither reader can tell, because each sees only its own
 * half and each half is internally coherent.
 *
 * ORDER IS COMPARED, NOT JUST MEMBERSHIP, because the order is the product. Two
 * lists holding the same addresses in different sequences are two different
 * sites to the person reading them.
 *
 * FAIL CLOSED, same as publishing private members with no key. Both are a
 * publisher about to write an event that misrepresents itself, and in both cases
 * the alternative to throwing is doing the wrong thing quietly.
 */
export function assertCollectionConsistent(
  tags: readonly (readonly string[])[],
  content: string,
  collectionKey: ContentKey
): void {
  if (!content) return;

  const fromTags: string[] = [];
  for (const t of tags) {
    if (t[0] === 'a' && typeof t[1] === 'string') fromTags.push(t[1]);
  }

  let sealed: SealedHalf;
  try {
    sealed = JSON.parse(decryptWithPageKey(content, collectionKey)) as SealedHalf;
  } catch {
    throw new Error('This collection key does not open this collection s sealed half');
  }
  if (!Array.isArray(sealed.members)) {
    throw new Error('The sealed half of this collection is not a member list');
  }

  const fromSealed = sealed.members.filter((m) => !m.private).map((m) => m.address);

  const same =
    fromTags.length === fromSealed.length && fromTags.every((a, i) => a === fromSealed[i]);
  if (!same) {
    throw new Error(
      `A collection's public order disagrees with its sealed order: tags say [${fromTags.join(
        ', '
      )}], sealed half says [${fromSealed.join(', ')}]`
    );
  }
}

/**
 * Read a collection event, with or without the key.
 *
 * WITHOUT THE KEY THIS STILL SUCCEEDS. A collection whose private half cannot be
 * opened is a real, usable collection of its public members, not an error, and
 * treating it as one would make every stranger's view of a mixed site a failure
 * page. The flag says an unopened half was present so the OWNER's view can tell
 * "there is nothing private here" from "I could not open it", which are very
 * different things to show the person who wrote it.
 */
export function parseCollectionEvent(
  event: Pick<UnsignedEvent, 'kind' | 'tags' | 'content'>,
  collectionKey?: ContentKey
): ParsedCollection | null {
  if (event.kind !== PAGE_COLLECTION_KIND) return null;

  const first = (name: string): string | undefined => {
    for (const t of event.tags) if (t[0] === name && typeof t[1] === 'string') return t[1];
    return undefined;
  };

  const identifier = first('d');
  if (!identifier) return null;

  const labels = new Map<string, string>();
  for (const t of event.tags) {
    if (t[0] === 'alabel' && typeof t[1] === 'string' && typeof t[2] === 'string') {
      labels.set(t[1], t[2]);
    }
  }

  const publicMembers: CollectionMemberRef[] = [];
  for (const t of event.tags) {
    if (t[0] !== 'a' || typeof t[1] !== 'string') continue;
    const member: CollectionMemberRef = { address: t[1], private: false };
    if (typeof t[2] === 'string' && t[2].length > 0) member.relay = t[2];
    const label = labels.get(t[1]);
    if (label) member.title = label;
    publicMembers.push(member);
  }

  const base: ParsedCollection = {
    identifier,
    title: first('title') ?? '',
    members: publicMembers,
    hasUnreadablePrivateHalf: false,
    memberKeys: new Map(),
    unopenedMemberKeys: [],
    ...(first('summary') ? { summary: first('summary')! } : {}),
    ...(first('type') ? { type: first('type')! } : {}),
    ...(first('front') ? { front: first('front')! } : {}),
    ...(first('theme') ? { theme: first('theme')! } : {}),
  };

  // A COLLECTION WITH NO PUBLIC MEMBER PUBLISHES ONLY ITS NAME, so there is no
  // title in the tags and nothing here to render. A reader without the key gets
  // the same answer they get for a page that is not for them.
  if (!event.content) return base.title ? base : null;
  if (!collectionKey) return base.title ? { ...base, hasUnreadablePrivateHalf: true } : null;

  let sealed: SealedHalf;
  try {
    const parsed: unknown = JSON.parse(decryptWithPageKey(event.content, collectionKey));
    if (typeof parsed !== 'object' || parsed === null || !Array.isArray((parsed as SealedHalf).members)) {
      return base.title ? { ...base, hasUnreadablePrivateHalf: true } : null;
    }
    sealed = parsed as SealedHalf;
  } catch {
    // The key we hold does not open this half, or somebody rewrote it. If there
    // is a public half it is still good and is what we show; if there is not,
    // there is nothing to show at all.
    return base.title ? { ...base, hasUnreadablePrivateHalf: true } : null;
  }

  const opened = openMemberKeys(sealed.mkeys, collectionKey);

  // The sealed half is authoritative for ORDER, because it is the only list that
  // has every member in it. It was written in one act with the tags, so the two
  // cannot have drifted.
  return {
    ...base,
    members: sealed.members,
    memberKeys: opened.keys,
    unopenedMemberKeys: opened.unopened,
    // The sealed title is authoritative because for a collection with no public
    // member it is the only one that exists.
    title: sealed.title || base.title,
    ...(sealed.summary ? { summary: sealed.summary } : {}),
  };
}

/** The tag name the owner's envelope uses, re-exported so a caller needs one import. */
export { OWNER_KEY_TAG };
